import { connect } from 'react-redux';
import { head, isEmpty } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import { openSmallModal } from '@actions/modal';
import { showErrorMessage } from '@actions/messageconfirmation';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import { ExportButton } from '@commons/ExportButton';
import { Filters } from '@commons/Filters';
import { formatNumber } from '@commons/DisplayNumber';
import {
  getCustomFilterIsStrategic,
  getCustomFilterMultipleCategory,
  getCustomFilterPastStocksIncludeRecipeStock,
  getCustomFilterPastStocksNegativeTheoreticalStock,
} from '@commons/utils/filtersFetches';
import { getPreviousDayOfStore } from '@commons/utils/date';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import { NestedList, SearchBar } from '@commons/utils/styledLibraryComponents';
import { PastDayDatePicker } from '@commons/DatePickers/PastDayDatePicker';
import StoresWithActiveProperty from '@commons/DropdownItems/StoresWithActiveProperty';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { useDatePickerState } from '@hooks/useDatePickerState';
import { useFiltersButtonState } from '@hooks/useFiltersButtonState';

import { getCentralKitchenStores } from '@selectors/stores';
import { getClientInfo } from '@selectors/client';

import { stock as stockService } from '@services/stock';
import centralService from '@services/central';

import ProductionAnalyticsToggle from '@src/routes/prod/components/AnalyticsContainer/components/ProductionAnalyticsToggle';

import { exportStocksAnalyticsPast } from '@stocks/StocksAnalytics/utils/exportStocksAnalyticsPast';
import { getCalendarInfo, sortByGapPercentage } from '@stocks/utils';
import { getPastInventoryOfEntityId } from '@stocks/StocksAnalytics/utils/getPastInventoryOfEntityId';
import { getPastStockData } from '@stocks/StocksAnalytics/utils/getPastStockData';
import { METRICS } from '@stocks/StocksAnalytics/utils/commons';
import { PAST_STOCKS_TYPE } from '@stocks/utils/constants';

import {
  computeTheoreticalTotal,
  computeRealTotal,
  computeCentralKitchenPastRecipesRealTotal,
  computeCentralKitchenPastRecipesTheoreticalTotal,
} from '@stocks/utils';
import {
  ConfigPanelContainer,
  ContentContainer,
  LeftSide,
  ListContainer,
  RightSide,
} from './styledComponents';
import { getPastRecipeInventoriesOfRecipe } from './utils/getPastRecipeInventoriesOfRecipe';
import { getPastStockDates } from './utils/getPastStocksDates';
import { Metric } from '@stocks/StocksAnalytics/commonStyledComponents';
import utilsColumns from './utils/columns';
import utilsFilters from './utils/getPastStocksFilters';

const getCentralKitchenPastStockModalCustomTogglesTemplate = ({
  pastStockType,
  customFilterIsStrategic,
  customFilterIncludeRecipeStock,
  customFilterNegativeTheoreticalStock,
}) => {
  if (pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS) {
    return [
      customFilterIsStrategic,
      customFilterIncludeRecipeStock,
      customFilterNegativeTheoreticalStock,
    ];
  }

  if (pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_RECIPES) {
    return [customFilterNegativeTheoreticalStock];
  }

  return null;
};

const getCentralKitchenPastStockModalCustomTogglesWithValues = ({
  pastStockType,
  customFilterIsStrategic,
  customFilterIncludeRecipeStock,
  customFilterNegativeTheoreticalStock,
  isStrategic,
  includeRecipeStock,
  allowNegativeTheoreticalStock,
}) => {
  if (pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS) {
    return [
      {
        ...customFilterIsStrategic,
        value: isStrategic,
      },
      {
        ...customFilterIncludeRecipeStock,
        value: includeRecipeStock,
      },
      {
        ...customFilterNegativeTheoreticalStock,
        value: allowNegativeTheoreticalStock,
      },
    ];
  }

  if (pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_RECIPES) {
    return [
      {
        ...customFilterNegativeTheoreticalStock,
        value: allowNegativeTheoreticalStock,
      },
    ];
  }

  return null;
};

const StocksAnalyticsPastNestedList = (props) => {
  const {
    currency,
    stores,
    pastStockType,
    showErrorMessage,
    openModalExportInfo,
    client: { clientId, storeName },
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(METRICS[0]);
  const [pastStocks, setPastStocks] = useState([]);
  const [displayedPastStocks, setDisplayedPastStocks] = useState([]);
  const [columns, setColumns] = useState(
    utilsColumns.getListConfig(pastStockType, selectedMetric, currency),
  );
  const [columnsModal] = useState(utilsColumns.getModalConfig());

  // SEARCH BAR & FILTERS STATES
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({});
  const filtersButtonState = useFiltersButtonState([]);
  const [applyFilters, setApplyFilters] = useState(false);
  const [formattedStores] = useState(
    stores.map((store) => ({
      ...store,
      renderValue: () => StoresWithActiveProperty(store),
    })),
  );
  const [selectedStore, setSelectedStore] = useState({});
  const [categoriesToChoose, setCategoriesToChoose] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(null);
  const [isStrategic, setIsStrategic] = useState(true);

  const [includeRecipeStock, setIncludeRecipeStock] = useState(false);

  const [allowNegativeTheoreticalStock, setAllowNegativeTheoreticalStock] = useState(false);

  // DATE PICKER STATES
  const datePickerState = useDatePickerState(moment().subtract(1, 'day'));
  const [recentDatesWithStocks, setRecentDatesWithStocks] = useState([]);
  const [focusedMonth, setFocusedMonth] = useState(moment());
  const [maxFutureDate, setMaxFutureDate] = useState(moment());

  const [customFilterMultipleCategories, setCustomFilterMultipleCategories] = useState(
    getCustomFilterMultipleCategory(
      categoriesToChoose,
      selectedCategories,
      setSelectedCategories,
      pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS, // specify if is relied on isStrategic toggle
    ),
  );

  const customFilterIsStrategic = getCustomFilterIsStrategic(isStrategic, setIsStrategic);

  const customFilterIncludeRecipeStock = getCustomFilterPastStocksIncludeRecipeStock(
    includeRecipeStock,
    setIncludeRecipeStock,
  );

  const customFilterNegativeTheoreticalStock = getCustomFilterPastStocksNegativeTheoreticalStock(
    allowNegativeTheoreticalStock,
    setAllowNegativeTheoreticalStock,
  );

  const [filtersModalParams, setFilterModalParams] = useState({
    applyFilters,
    setApplyFilters,
    filters,
    setFilters,
    stores: formattedStores,
    selectedStore,
    setSelectedStore,
    customToggles: getCentralKitchenPastStockModalCustomTogglesTemplate({
      pastStockType,
      customFilterIsStrategic,
      customFilterIncludeRecipeStock,
      customFilterNegativeTheoreticalStock,
    }),
    customMultipleDropDowns: [customFilterMultipleCategories],
  });

  /** USE EFFECTS */
  useEffect(() => {
    if (!selectedStore) {
      // no selectedStore means first load
      const firstLoadedStore = head(stores);
      const previousDayOfStore = getPreviousDayOfStore(firstLoadedStore);

      setMaxFutureDate(previousDayOfStore);
      datePickerState.setDate(previousDayOfStore);

      return;
    }

    const previousDayOfStore = getPreviousDayOfStore(selectedStore);

    setMaxFutureDate(previousDayOfStore);
    datePickerState.setDate(previousDayOfStore);
  }, [stores, selectedStore]);

  useEffect(() => {
    if (isEmpty(stores)) {
      return;
    }
    (async function loadData() {
      setIsLoading(true);

      const activeStore = formattedStores.find(({ active }) => active) || head(formattedStores);
      setSelectedStore(activeStore);

      const updatedFilters =
        pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_RECIPES
          ? await utilsFilters.getRecipesPastStockFilters(clientId, formattedStores, {
              setCategoriesToChoose,
              setSelectedCategories,
              customFilterNegativeTheoreticalStock,
              customFilterMultipleCategories,
              showErrorMessage,
            })
          : await utilsFilters.getIngredientsPastStockFilters(clientId, formattedStores, {
              setCategoriesToChoose,
              customFilterIsStrategic,
              customFilterIncludeRecipeStock,
              customFilterNegativeTheoreticalStock,
              customFilterMultipleCategories,
              showErrorMessage,
            });

      setFilters(updatedFilters);
      setApplyFilters(true);
      setIsLoading(false);
    })();
  }, [stores]);

  useEffect(() => {
    if (isEmpty(selectedStore) && !isLoading) {
      return;
    }

    (async function loadPastStocksDates() {
      const recentDates = await getPastStockDates(pastStockType, focusedMonth, selectedStore.id, {
        showErrorMessage,
      });
      setRecentDatesWithStocks(recentDates);
    })();
  }, [focusedMonth, selectedStore, datePickerState.date]);

  useEffect(() => {
    if (applyFilters) {
      refreshPastStockData();
    }
  }, [applyFilters]);

  useEffect(() => {
    if (!!datePickerState.date && !isLoading) {
      refreshPastStockData();
    }
  }, [datePickerState.date]);

  useEffect(() => {
    if (!searchInput) {
      setDisplayedPastStocks(pastStocks);
      return;
    }
    const filteredPastStocks = pastStocks.filter(({ name }) =>
      name.toLowerCase().includes(searchInput.toLowerCase()),
    );
    setDisplayedPastStocks(filteredPastStocks);
  }, [searchInput]);

  useEffect(() => {
    setFilterModalParams({
      ...filtersModalParams,
      selectedStore,
      stores: formattedStores,
      filters,
      customMultipleDropDowns: [
        {
          ...customFilterMultipleCategories,
          selectedItems: selectedCategories,
          originalSelectedItems: selectedCategories,
          list: categoriesToChoose,
        },
      ],
      customToggles: getCentralKitchenPastStockModalCustomTogglesWithValues({
        pastStockType,
        customFilterIsStrategic,
        customFilterIncludeRecipeStock,
        customFilterNegativeTheoreticalStock,
        isStrategic,
        includeRecipeStock,
        allowNegativeTheoreticalStock,
      }),
    });
  }, [
    stores,
    filters,
    categoriesToChoose,
    selectedStore,
    selectedCategories,
    isStrategic,
    includeRecipeStock,
    allowNegativeTheoreticalStock,
    pastStockType,
  ]);

  useEffect(() => {
    if (selectedCategories) {
      setCustomFilterMultipleCategories({
        ...customFilterMultipleCategories,
        selectedItems: selectedCategories,
      });
    }
  }, [selectedCategories]);

  useEffect(() => {
    setColumns(
      utilsColumns.getListConfig(
        pastStockType,
        selectedMetric,
        currency,
        allowNegativeTheoreticalStock,
      ),
    );
    if (!isEmpty(selectedStore)) {
      refreshPastStockData();
    }
  }, [pastStockType, selectedMetric, currency, allowNegativeTheoreticalStock]);

  useEffect(() => {
    (async function refreshStockGraph() {
      const formattedPastStocks = await formatData(displayedPastStocks);

      setDisplayedPastStocks(formattedPastStocks);
    })();
  }, [selectedMetric.key]);

  /** FUNCTIONS */

  const formatData = async (data) =>
    data.map((item) => ({
      ...item,
      type: i18next.t(
        pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS
          ? 'GENERAL.INGREDIENT'
          : 'GENERAL.RECIPE',
      ),
      modalContent: async () =>
        pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS
          ? await getPastInventoryOfEntityId(
              item.entityId,
              selectedStore,
              selectedMetric,
              currency,
              PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS,
              showErrorMessage,
            )
          : await getPastRecipeInventoriesOfRecipe(
              item.entityId,
              selectedStore.id,
              selectedMetric,
              currency,
              showErrorMessage,
            ),
      graphContent: async () =>
        await getPastStockData(
          item,
          moment(datePickerState.date).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
          selectedStore,
          selectedMetric,
          currency,
          pastStockType,
          showErrorMessage,
          includeRecipeStock,
          allowNegativeTheoreticalStock,
        ),
    }));

  const refreshPastStockData = async () => {
    if (isEmpty(selectedStore)) {
      return;
    }
    setIsLoading(true);

    const categoryIds = selectedCategories ? selectedCategories.map(({ id }) => id) : [];

    const selectedStoreId = selectedStore.id;

    const selectedDateToFetch = moment(datePickerState.date).format(
      DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
    );

    try {
      const data =
        pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS
          ? await stockService.getPastStocksOfIngredientsByCategories(
              selectedStoreId,
              selectedDateToFetch,
              categoryIds,
              isStrategic,
              includeRecipeStock,
              allowNegativeTheoreticalStock,
            )
          : await centralService.getRecipesPastAnalyticsList(
              selectedStoreId,
              selectedDateToFetch,
              categoryIds,
              false, // filter isStrategic does not exist for CK recipes
              allowNegativeTheoreticalStock,
            );

      const formattedData = await formatData(data);

      setPastStocks(formattedData);
      setDisplayedPastStocks(formattedData);
    } catch {
      setPastStocks([]);
      setDisplayedPastStocks([]);
      showErrorMessage(i18next.t('STOCKS.PAST_STOCKS.DATA_FETCH_ERROR'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContentContainer>
      <ConfigPanelContainer>
        <LeftRightSplitter
          left={
            <LeftSide>
              <SearchBar
                placeholder={i18next.t('GENERAL.SEARCH')}
                setValue={setSearchInput}
                value={searchInput}
              />
              <Filters
                filtersButtonState={filtersButtonState}
                filtersModalParams={filtersModalParams}
                filterText={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
              />
              <PastDayDatePicker
                authorizedDates={recentDatesWithStocks}
                calendarInfo={getCalendarInfo(storeName)}
                date={datePickerState.date}
                maxFutureDate={maxFutureDate}
                numberOfMonths={2}
                onDateChange={datePickerState.setDate}
                onNextMonthClick={(nextMonth) => setFocusedMonth(nextMonth)}
                onPrevMonthClick={(prevMonth) => setFocusedMonth(prevMonth)}
              />
              <ProductionAnalyticsToggle
                choices={METRICS}
                selectedChoice={selectedMetric}
                setSelectedChoice={setSelectedMetric}
              />
              <Metric extraPaddingLeft={'24px'}>
                <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_MICRO_HEIGHT_12}>
                  {i18next.t('STOCKS.PAST_STOCKS.THEORETICAL_TOTAL')}
                </Text>
                {isLoading ? (
                  <DeepsightComponentLoader height={16} width={16} />
                ) : (
                  <Text>
                    {formatNumber(
                      pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS
                        ? computeTheoreticalTotal(displayedPastStocks)
                        : computeCentralKitchenPastRecipesTheoreticalTotal(displayedPastStocks),
                      currency.numberDecimals,
                    )}{' '}
                    {currency.alphabeticCode}
                  </Text>
                )}
              </Metric>
              <Metric>
                <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_MICRO_HEIGHT_12}>
                  {i18next.t('STOCKS.PAST_STOCKS.REAL_TOTAL')}
                </Text>
                {isLoading ? (
                  <DeepsightComponentLoader height={16} width={16} />
                ) : (
                  <Text>
                    {formatNumber(
                      pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS
                        ? computeRealTotal(displayedPastStocks)
                        : computeCentralKitchenPastRecipesRealTotal(displayedPastStocks),
                      currency.numberDecimals,
                    )}{' '}
                    {currency.alphabeticCode}
                  </Text>
                )}
              </Metric>
            </LeftSide>
          }
        />

        <RightSide>
          <ExportButton
            handleClick={() =>
              exportStocksAnalyticsPast(
                displayedPastStocks,
                openModalExportInfo,
                pastStockType,
                selectedStore.name,
                datePickerState.date,
                storeName,
                allowNegativeTheoreticalStock,
              )
            }
            isInpulseOnly
          />
        </RightSide>
      </ConfigPanelContainer>
      <ListContainer>
        <NestedList
          customModalIcon={'icon-stock'}
          customSortFunc={sortByGapPercentage}
          customSortProperty={'stockGapPercentage'}
          data={displayedPastStocks}
          defaultOrderBy={'stockGapPercentage'}
          defaultOrderType={'desc'}
          graphEmptyStateLabel={i18next.t(
            pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS
              ? 'STOCKS.PAST_STOCKS_INGREDIENTS.GRAPH_EMPTY_STATE_LABEL'
              : 'STOCKS.PAST_STOCKS_RECIPES.GRAPH_EMPTY_STATE_LABEL',
          )}
          headers={columns}
          isLoading={isLoading}
          labelModalEmptyState={i18next.t(
            pastStockType === PAST_STOCKS_TYPE.CENTRAL_KITCHEN_INGREDIENTS
              ? 'STOCKS.PAST_STOCKS_INGREDIENTS.LAST_INVENTORIES_GRAPH_EMPTY_STATE'
              : 'STOCKS.PAST_STOCKS_RECIPES.LAST_INVENTORIES_GRAPH_EMPTY_STATE',
          )}
          minWidth={'978px'}
          modalHeaders={columnsModal}
          modalTitle={i18next.t('STOCKS.PAST_STOCKS.LAST_INVENTORIES')}
        />
      </ListContainer>
    </ContentContainer>
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  stores: getCentralKitchenStores(state.baseReducer.stores),
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openModalExportInfo: (params) => {
    dispatch(openSmallModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(StocksAnalyticsPastNestedList);
